import {
  Component,
  computed,
  inject,
  Input,
  output,
  signal,
  ViewChild,
} from '@angular/core';
import { Dialog } from '@angular/cdk/dialog';
import { KfmInfoDialogComponent } from '@shared/ui/components/features/dialogs/info-dialog/kfm-info-dialog.component';
import { KfmIbanInputFieldComponent } from './iban-input-field/kfm-iban-input-field.component';
import { IBANDetail } from './models/iban-detail-model';
import { IBANCulture } from './iban-input-field/iban/iban-culture';
import { Subscription } from 'rxjs';
import { CommonModule } from '@angular/common';
import { BankAccount } from '@modules/contact/models/bank-account.model';
import { ContactType } from '@hsetu/ngui-views';
import {
  HsDateTimePickerModule,
  HsFormFieldModule,
  HsInputModule,
} from '@hsetu/ngui-components';
import {
  ACCOUNT_HOLDER_LABEL,
  ACCOUNT_NUMBER_LABEL,
  BANK_NAME_LABEL,
  BIC_LABEL,
  BLZ_LABEL,
  MANDATE_REFERENCE_DATE_LABEL,
  MANDATE_REFERENCE_NUMBER_LABEL,
} from '@modules/contact/i18n/label-text-constants';

@Component({
  selector: 'kfm-contact-bank-account-form',
  imports: [
    CommonModule,
    KfmIbanInputFieldComponent,
    HsFormFieldModule,
    HsInputModule,
    HsDateTimePickerModule,
  ],
  templateUrl: './kfm-contact-bank-account-form.component.html',
  host: { class: 'kfm-contact-bank-account' },
})
export class KfmContactBankAccountFormComponent {
  @ViewChild(KfmIbanInputFieldComponent)
  private _ibanInputField!: KfmIbanInputFieldComponent;

  private readonly _dialog = inject(Dialog);
  private validitySubscription?: Subscription;
  private _ibanDetail!: IBANDetail;
  private _bankAccount: BankAccount | null = null;

  protected readonly accountHolderLabel = ACCOUNT_HOLDER_LABEL;
  protected readonly bicLabel = BIC_LABEL;
  protected readonly accountNumberLabel = ACCOUNT_NUMBER_LABEL;
  protected readonly blzLabel = BLZ_LABEL;
  protected readonly bankNameLabel = BANK_NAME_LABEL;
  protected readonly mandateReferenceNumberLabel =
    MANDATE_REFERENCE_NUMBER_LABEL;
  protected readonly mandateReferenceDateLabel = MANDATE_REFERENCE_DATE_LABEL;

  // Input to receive the contact type for determining if we're dealing with a tenant (mandant)
  @Input() contactType!: ContactType;

  // Computed property to determine if mandate reference fields should be shown
  protected readonly showMandateReferenceFields = computed(() => {
    // Hide mandate reference fields for tenants (mandants)
    return this.contactType !== ContactType.Tenant;
  });

  protected readonly internalBankAccount = signal<BankAccount | null>(null);

  protected readonly iban = computed(() => {
    const bankAccount = this.internalBankAccount();

    return bankAccount ? (bankAccount.ibanCode ?? '') : '';
  });

  protected readonly swiftCode = computed(() => {
    const bankAccount = this.internalBankAccount();

    return bankAccount ? (bankAccount.swiftCode ?? '') : '';
  });

  protected readonly accountNumber = computed(() => {
    const bankAccount = this.internalBankAccount();

    return bankAccount ? (bankAccount.accountNumber ?? '') : '';
  });

  protected readonly bankCodeNumber = computed(() => {
    const bankAccount = this.internalBankAccount();
    return bankAccount ? (bankAccount.bankCodeNumber ?? '') : '';
  });

  protected readonly bankName = computed(() => {
    const bankAccount = this.internalBankAccount();
    return bankAccount ? (bankAccount.bankName ?? '') : '';
  });

  protected readonly accountHolder = computed(() => {
    const bankAccount = this.internalBankAccount();
    return bankAccount ? (bankAccount.accountHolder ?? '') : '';
  });

  protected readonly mandateReferenceNumber = computed(() => {
    const bankAccount = this.internalBankAccount();
    return bankAccount ? (bankAccount.mandateReferenceNumber ?? '') : '';
  });

  protected readonly mandateReferenceDate = computed(() => {
    const bankAccount = this.internalBankAccount();
    return bankAccount && bankAccount.mandateReferenceDate
      ? new Date(bankAccount.mandateReferenceDate)
      : undefined;
  });

  @Input() set bankAccount(val: BankAccount | undefined) {
    this.internalBankAccount.set(val ?? null);
    this._bankAccount = val ?? null;

    if (this.validitySubscription) this.validitySubscription.unsubscribe();

    this._ibanDetail = new IBANDetail(
      IBANCulture.DE,
      this.internalBankAccount()?.ibanCode ?? '',
    );

    this.validitySubscription = this._ibanDetail.validityChanged.subscribe(
      (ok: boolean) => {
        const bankAccount = this.internalBankAccount();
        if (ok) {
          this.internalBankAccount.update(() => {
            if (!bankAccount) return null;
            return {
              ...bankAccount,
              ibanCode: this._ibanDetail.iban,
              accountNumber: this._ibanDetail.accountNumber,
              bankCodeNumber: this._ibanDetail.bankCode,
              bankName: this._ibanDetail.bankName,
              swiftCode: this._ibanDetail.swiftCode,
            };
          });

          this.updateProperty('ibanCode', this._ibanDetail.iban);
          this.updateProperty('accountNumber', this._ibanDetail.accountNumber);
          this.updateProperty('bankCodeNumber', this._ibanDetail.bankCode);
          this.updateProperty('bankName', this._ibanDetail.bankName);
          this.updateProperty('swiftCode', this._ibanDetail.swiftCode);
        } else {
          const cleaned = this._ibanDetail.iban
            .replace(/\s+/g, '')
            .replace(/[_]/g, '');

          if (cleaned.length > 0) {
            const dialogRef = this._dialog.open(KfmInfoDialogComponent, {
              data: {
                title: 'IBAN Validierung',
                text: 'Die eingegebene IBAN ist ungültig.',
              },
            });
            dialogRef.closed.subscribe(() => {
              this._ibanInputField?.focusInput();
            });
          }

          this.internalBankAccount.update(() => {
            if (!bankAccount) return null;
            return {
              ...bankAccount,
              ibanCode: undefined,
              accountNumber: undefined,
              bankCodeNumber: undefined,
              bankName: undefined,
              swiftCode: undefined,
            };
          });

          this.updateProperty('ibanCode', undefined);
          this.updateProperty('accountNumber', undefined);
          this.updateProperty('bankCodeNumber', undefined);
          this.updateProperty('bankName', undefined);
          this.updateProperty('swiftCode', undefined);
        }

        this.update();
      },
    );
  }

  public readonly bankAccountChanged = output<BankAccount>();

  protected get ibanDetail(): IBANDetail {
    return this._ibanDetail;
  }

  protected async update() {
    const bankAccount = this.internalBankAccount();
    this.bankAccountChanged.emit(bankAccount);
  }

  /** Start date date-input changed */
  protected onMandateReferenceDateChanged(event: unknown) {
    if (!event || (event as string).trim().length === 0) {
      return;
    }

    const bankAccount = this.internalBankAccount();

    if (!bankAccount) return;

    bankAccount.mandateReferenceDate = new Date(event as string);

    this.updateProperty(
      'mandateReferenceDate',
      bankAccount.mandateReferenceDate,
    );

    this.update();
  }

  /** Update a specific property of the bank account */
  protected updateProperty<K extends keyof BankAccount>(
    key: K,
    value: BankAccount[K],
  ) {
    if (this._bankAccount) {
      this._bankAccount[key] = value;
      this.update();
    }
  }
}
